import React from 'react';
import { TrendingUp, TrendingDown, Minus, Zap } from 'lucide-react';
import ConfidenceGauge from '../trading/ConfidenceGauge';
import type { TradeEvalData } from '../../types/expert';

interface TradeEvalCardProps {
  data: TradeEvalData;
}

const signalConfig = {
  BUY: { color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30', icon: TrendingUp },
  SELL: { color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: TrendingDown },
  HOLD: { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', icon: Minus },
};

const TradeEvalCard: React.FC<TradeEvalCardProps> = ({ data }) => {
  const config = signalConfig[data.signal as keyof typeof signalConfig] || signalConfig.HOLD;
  const Icon = config.icon;

  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) return '—';
    return price.toFixed(price >= 100 ? 2 : 5);
  };

  return (
    <div className={`rounded-xl border p-4 ${config.bg}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${config.bg}`}>
            <Icon size={18} className={config.color} />
          </div>
          <span className="text-base font-bold text-white">{data.symbol}</span>
        </div>
        <span className={`text-xl font-bold ${config.color}`}>{data.signal}</span>
      </div>

      {/* Confidence */}
      <div className="mb-3">
        <ConfidenceGauge value={data.confidence} />
      </div>

      {/* Entry / SL / TP grid */}
      {data.signal !== 'HOLD' && (data.entry || data.stop_loss || data.take_profit) && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
            <p className="text-[10px] text-slate-400 mb-0.5">Entry</p>
            <p className="text-xs font-semibold text-white font-mono tabular-nums">
              {formatPrice(data.entry)}
            </p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
            <p className="text-[10px] text-red-400 mb-0.5">Stop Loss</p>
            <p className="text-xs font-semibold text-red-300 font-mono tabular-nums">
              {formatPrice(data.stop_loss)}
            </p>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-2 text-center">
            <p className="text-[10px] text-emerald-400 mb-0.5">Take Profit</p>
            <p className="text-xs font-semibold text-emerald-300 font-mono tabular-nums">
              {formatPrice(data.take_profit)}
            </p>
          </div>
        </div>
      )}

      {/* R:R and Session info */}
      <div className="flex items-center gap-3 mb-3 text-xs text-slate-400">
        {data.risk_reward_ratio != null && (
          <span>R:R <span className="text-white font-medium">{data.risk_reward_ratio}:1</span></span>
        )}
        {data.session && (
          <span className="capitalize">{data.session.replace('_', ' ')}</span>
        )}
      </div>

      {/* Timeframe alignment pills */}
      {data.timeframe_alignment && Object.keys(data.timeframe_alignment).length > 0 && (
        <div className="flex gap-1 mb-3 flex-wrap">
          {(['h4', 'h1', 'm15', 'm5'] as const).map((tf) => {
            const val = data.timeframe_alignment?.[tf];
            if (!val) return null;
            const tfColor =
              val === 'bullish'
                ? 'bg-emerald-500/20 text-emerald-400'
                : val === 'bearish'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-slate-700 text-slate-400';
            return (
              <span key={tf} className={`text-[10px] px-1.5 py-0.5 rounded ${tfColor}`}>
                {tf.toUpperCase()}
              </span>
            );
          })}
          {data.timeframe_alignment?.aligned && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-400">
              <Zap size={8} className="inline mr-0.5" />
              Aligned
            </span>
          )}
        </div>
      )}

      {/* Reasoning */}
      {data.reasoning && (
        <p className="text-xs text-slate-300 mb-2 leading-relaxed">{data.reasoning}</p>
      )}

      {/* Warnings */}
      {data.warnings && data.warnings.length > 0 && (
        <div className="space-y-0.5">
          {data.warnings.map((w, i) => (
            <p key={i} className="text-[10px] text-amber-400/80">
              ⚠ {w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default TradeEvalCard;
